import { Badge, Typography } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { cn } from "@/lib/utils/cn";
import {
  getDeterministicColorFromId,
  type DeterministicColor,
} from "@/lib/utils/deterministic-color";

const initialsColorClassNames: Record<DeterministicColor, string> = {
  blue: "bg-sky-500",
  green: "bg-teal-500",
  orange: "bg-amber-500",
  purple: "bg-violet-500",
  red: "bg-rose-500",
  yellow: "bg-yellow-500",
};

function initialsFromDisplayName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

type LeadsAssigneeSummaryProps = {
  names: string[];
  className?: string;
};

export function LeadsAssigneeSummary({
  names,
  className,
}: LeadsAssigneeSummaryProps) {
  if (names.length === 0) {
    return (
      <div
        className={cn(
          "text-muted-foreground flex max-w-[160px] min-w-0 shrink-0 items-center gap-1.5",
          className,
        )}
      >
        <div
          className="border-muted-foreground/40 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dashed"
          aria-hidden
        />
        <Typography className="truncate text-xs italic">Unassigned</Typography>
      </div>
    );
  }

  const primary = names[0] ?? "";
  const rest = names.slice(1);
  const color = getDeterministicColorFromId(primary);
  const initials = initialsFromDisplayName(primary);
  const tooltipMessage =
    rest.length > 0 ? rest.map((n) => n.trim()).join("\n") : "";

  return (
    <div
      className={cn(
        "flex max-w-[180px] min-w-0 shrink-0 items-center gap-1.5",
        className,
      )}
    >
      <div
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] leading-none font-medium text-white",
          initialsColorClassNames[color],
        )}
        aria-hidden
      >
        {initials}
      </div>
      <TooltipWrapper message={tooltipMessage}>
        <div className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs">
          <span className="truncate">{primary}</span>
          {rest.length > 0 ? (
            <Badge className="h-4 shrink-0 px-1 py-0 text-[9px] leading-none font-medium">
              +{rest.length}
            </Badge>
          ) : null}
        </div>
      </TooltipWrapper>
    </div>
  );
}
